import { useState, useCallback, useRef, useEffect } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Upload, X, FileText, Image as ImageIcon, Loader2, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getActiveTenant } from '@/lib/active-tenant';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { DocumentPreviewDialog } from './DocumentPreviewDialog';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';
const MAX_SIZE_MB = 10;
const MAX_FILES = 20;

export interface PaymentDocument {
  id: number;
  originalName: string;
  fileName: string;
  mimeType: string;
  size: number;
  url: string;
  createdAt?: string;
}

interface PaymentDocumentsUploadProps {
  /**
   * Уже привязанные к платежу документы (read-only данные с backend).
   * При `paymentId !== null` компонент сам подгружает их через GET и
   * управляет add/remove через POST/DELETE.
   * При `paymentId === null` (форма создания) — файлы только аккумулируются
   * в локальном состоянии, родитель забирает `fileIds` через `onPendingChange`.
   */
  paymentId: number | null;
  /**
   * Колбэк для режима создания: возвращает массив id файлов, загруженных
   * в этой сессии и ещё не привязанных к платежу.
   */
  onPendingChange?: (fileIds: number[]) => void;
  className?: string;
  disabled?: boolean;
}

interface UploadProgressEntry {
  tempId: string;
  name: string;
  progress: number;
  error?: string;
}

export function PaymentDocumentsUpload({
  paymentId,
  onPendingChange,
  className,
  disabled,
}: PaymentDocumentsUploadProps) {
  const apiUrl = useApiUrl();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [documents, setDocuments] = useState<PaymentDocument[]>([]);
  const [pendingFileIds, setPendingFileIds] = useState<number[]>([]);
  const [uploads, setUploads] = useState<UploadProgressEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<PaymentDocument | null>(null);

  const getAuthHeader = useCallback((): HeadersInit => {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Загрузка списка документов уже существующего платежа
  const fetchDocuments = useCallback(async () => {
    if (paymentId === null) { return; }
    setIsLoading(true);
    try {
      const res = await fetch(`${apiUrl}/payments/${paymentId}/documents`, {
        headers: getAuthHeader(),
      });
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setDocuments(json.data);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки документов');
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, getAuthHeader, paymentId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    onPendingChange?.(pendingFileIds);
  }, [pendingFileIds, onPendingChange]);

  // Загрузка одного файла на сервер: POST /upload/document
  const uploadFile = useCallback(async (file: File): Promise<{ id: number; url: string }> => {
    return new Promise((resolve, reject) => {
      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setUploads((prev) => [...prev, { tempId, name: file.name, progress: 0 }]);

      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiUrl}/upload/document`);

      const token = localStorage.getItem('accessToken');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      // X-Tenant-Id: XHR минует global fetch interceptor — добавляем вручную,
      // иначе на узб (club) upload падал в default-тенант.
      const tenant = getActiveTenant();
      if (tenant) {
        xhr.setRequestHeader('X-Tenant-Id', tenant);
      }

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) { return; }
        const progress = Math.round((event.loaded / event.total) * 100);
        setUploads((prev) => prev.map((u) => u.tempId === tempId ? { ...u, progress } : u));
      };

      xhr.onload = () => {
        setUploads((prev) => prev.filter((u) => u.tempId !== tempId));
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success && response.data?.id) {
              resolve({ id: response.data.id, url: response.data.url });
            } else {
              reject(new Error(response.errors?.[0] ?? 'Ошибка загрузки'));
            }
          } catch {
            reject(new Error('Ошибка обработки ответа сервера'));
          }
        } else {
          reject(new Error(`Ошибка загрузки: ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        setUploads((prev) => prev.filter((u) => u.tempId !== tempId));
        reject(new Error('Ошибка сети'));
      };

      xhr.send(formData);
    });
  }, [apiUrl]);

  // Привязка fileId к существующему платежу
  const bindToPayment = useCallback(async (fileIds: number[]) => {
    if (paymentId === null || fileIds.length === 0) { return; }
    const res = await fetch(`${apiUrl}/payments/${paymentId}/documents`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileIds }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.errors?.[0] ?? 'Ошибка привязки документов');
    }
    if (Array.isArray(json.data)) {
      setDocuments(json.data);
    }
  }, [apiUrl, getAuthHeader, paymentId]);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    setError(null);
    const fileArr = [...files];
    const totalAfter = documents.length + pendingFileIds.length + fileArr.length;
    if (totalAfter > MAX_FILES) {
      setError(`Максимум ${MAX_FILES} документов на платёж`);
      return;
    }

    const accepted = fileArr.filter((f) => {
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        setError(`Файл "${f.name}" больше ${MAX_SIZE_MB} МБ`);
        return false;
      }
      const allowedTypes = ACCEPT.split(',');
      if (!allowedTypes.includes(f.type)) {
        setError(`Файл "${f.name}" имеет недопустимый тип ${f.type}`);
        return false;
      }
      return true;
    });

    if (accepted.length === 0) { return; }

    try {
      const uploaded = await Promise.all(accepted.map((f) => uploadFile(f)));
      const newIds = uploaded.map((u) => u.id);

      if (paymentId !== null) {
        await bindToPayment(newIds);
      } else {
        setPendingFileIds((prev) => [...prev, ...newIds]);
        // визуально сразу показываем, чтоб пользователь видел загруженное
        setDocuments((prev) => [
          ...prev,
          ...accepted.map((f, i) => ({
            id: uploaded[i].id,
            originalName: f.name,
            fileName: f.name,
            mimeType: f.type,
            size: f.size,
            url: uploaded[i].url,
          })),
        ]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
    }
  }, [documents.length, pendingFileIds.length, uploadFile, bindToPayment, paymentId]);

  const handleRemove = useCallback(async (fileId: number) => {
    setError(null);
    if (paymentId !== null) {
      try {
        const res = await fetch(`${apiUrl}/payments/${paymentId}/documents/${fileId}`, {
          method: 'DELETE',
          headers: getAuthHeader(),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json.errors?.[0] ?? 'Ошибка удаления');
        }
        setDocuments((prev) => prev.filter((d) => d.id !== fileId));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Ошибка удаления');
      }
    } else {
      // pending-режим: просто убираем локально
      setPendingFileIds((prev) => prev.filter((id) => id !== fileId));
      setDocuments((prev) => prev.filter((d) => d.id !== fileId));
    }
  }, [apiUrl, getAuthHeader, paymentId]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) { return; }
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [disabled, handleFiles]);

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) { return `${bytes} Б`; }
    if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} КБ`; }
    return `${(bytes / 1024 / 1024).toFixed(2)} МБ`;
  };

  const renderIcon = (mime: string) => {
    if (mime.startsWith('image/')) {
      return <ImageIcon className="h-4 w-4 text-blue-500" />;
    }
    return <FileText className="h-4 w-4 text-red-500" />;
  };

  const totalCount = documents.length + uploads.length;
  const canUpload = totalCount < MAX_FILES;

  return (
    <div className={cn('space-y-2', className)}>
      <button
        type="button"
        onDragEnter={(e) => { e.preventDefault(); if (!disabled) { setIsDragging(true); } }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) { setIsDragging(true); } }}
        onDrop={handleDrop}
        onClick={() => !disabled && canUpload && inputRef.current?.click()}
        disabled={disabled || !canUpload}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-sm transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-muted',
          disabled || !canUpload ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted/40',
        )}
      >
        <Upload className="h-5 w-5 text-muted-foreground" />
        <div className="text-muted-foreground text-xs text-center">
          Перетащите файлы или нажмите для выбора
          <br />
          <span className="text-[10px]">JPG/PNG/WEBP/GIF/PDF · до {MAX_SIZE_MB} МБ · максимум {MAX_FILES} файлов</span>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          disabled={disabled || !canUpload}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              handleFiles(e.target.files);
              e.target.value = '';
            }
          }}
        />
      </button>

      {error && (
        <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded px-2 py-1">
          {error}
        </div>
      )}

      {uploads.length > 0 && (
        <div className="space-y-1">
          {uploads.map((u) => (
            <div key={u.tempId} className="flex items-center gap-2 text-xs">
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              <span className="flex-1 truncate">{u.name}</span>
              <Progress value={u.progress} className="w-20 h-1.5" />
              <span className="w-8 text-right text-muted-foreground">{u.progress}%</span>
            </div>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Загружаю документы…
        </div>
      )}

      {!isLoading && documents.length > 0 && (
        <ul className="space-y-1">
          {documents.map((doc) => (
            <li key={doc.id} className="group flex items-center gap-2 rounded border bg-muted/30 px-2 py-1.5 text-xs transition-colors hover:bg-muted/60">
              <button
                type="button"
                onClick={() => setPreviewDoc(doc)}
                className="flex flex-1 items-center gap-2 min-w-0 text-left"
                title="Просмотреть"
              >
                {renderIcon(doc.mimeType)}
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium group-hover:text-primary" title={doc.originalName}>{doc.originalName}</div>
                  <div className="text-muted-foreground text-[10px]">{doc.mimeType} · {formatSize(doc.size)}</div>
                </div>
              </button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setPreviewDoc(doc); }}
                className="h-6 w-6 p-0"
                title="Просмотреть"
              >
                <Eye className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleRemove(doc.id); }}
                disabled={disabled}
                className="h-6 w-6 p-0"
                title="Удалить"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <DocumentPreviewDialog
        document={previewDoc}
        open={previewDoc !== null}
        onOpenChange={(open) => { if (!open) { setPreviewDoc(null); } }}
      />


      {!isLoading && documents.length === 0 && (
        <div className="text-xs text-muted-foreground italic">Документы не прикреплены</div>
      )}
    </div>
  );
}
